(() => {
  'use strict';
  const key = Symbol.for('seldravinpack.collection.initialize');
  if (window[key]) { window[key](document); return; }
  const instances = new Map();
  function create(root) {
    const form = root.querySelector('[data-collection-form]');
    const sidebar = root.querySelector('[data-filter-sidebar]');
    const panel = root.querySelector('[data-filter-panel]');
    const dialog = root.querySelector('#filter-dialog');
    const slot = root.querySelector('#mobile-filter-slot');
    const trigger = root.querySelector('#open-filters');
    const mobile = matchMedia('(max-width:767px)');
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    const controller = new AbortController();
    const listen = (node, event, fn) => node.addEventListener(event, fn, { signal: controller.signal });
    const config = JSON.parse(root.querySelector('[data-products-config]').textContent);
    let animation = null;
    function close(animate = true) {
      animation?.cancel();
      if (!dialog.open) return;
      if (!animate || reduced.matches) { dialog.close(); return; }
      animation = dialog.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(24px)' }], { duration: 180, easing: 'ease-in' });
      animation.onfinish = () => { animation = null; dialog.close(); };
    }
    listen(trigger, 'click', () => {
      animation?.cancel();
      slot.append(panel);
      if (!dialog.open) dialog.showModal();
      if (!reduced.matches) animation = dialog.animate([{ opacity: 0, transform: 'translateX(24px)' }, { opacity: 1, transform: 'translateX(0)' }], { duration: 220, easing: 'ease-out' });
    });
    listen(dialog, 'close', () => { sidebar.append(panel); if (mobile.matches && trigger.isConnected) trigger.focus(); });
    listen(dialog, 'cancel', event => { event.preventDefault(); close(); });
    listen(dialog, 'click', event => {
      if (event.target.closest('[data-close-filters]')) close();
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) close();
    });
    listen(mobile, 'change', () => { if (!mobile.matches) close(false); });
    listen(reduced, 'change', () => { animation?.finish(); });
    const priceFilters = [...form.querySelectorAll('[data-price-filter]')].map(group => {
      const min = group.querySelector('[data-price-min]'), max = group.querySelector('[data-price-max]');
      const lower = group.querySelector('[data-range-min]'), upper = group.querySelector('[data-range-max]');
      const track = group.querySelector('[data-price-track]');
      const limit = Math.max(0, Number(group.dataset.priceLimit) || 0);
      let money;
      try { money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: group.dataset.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
      catch { money = new Intl.NumberFormat(document.documentElement.lang || 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
      const values = () => [min.value.trim() === '' ? 0 : Number(min.value), max.value.trim() === '' ? limit : Number(max.value)];
      function validate() {
        const [low, high] = values();
        const badLow = !Number.isFinite(low) || low < 0 || low > limit;
        const badHigh = !Number.isFinite(high) || high < 0 || high > limit;
        min.setCustomValidity(badLow ? config.priceBounds : low > high ? config.priceError : '');
        max.setCustomValidity(badHigh ? config.priceBounds : '');
        return !badLow && !badHigh && low <= high && min.validity.patternMismatch === false && max.validity.patternMismatch === false;
      }
      function paint(low, high) {
        lower.value = low; upper.value = high;
        track.style.setProperty('--range-start', `${limit ? low / limit * 100 : 0}%`);
        track.style.setProperty('--range-end', `${limit ? high / limit * 100 : 0}%`);
        lower.style.zIndex = low === limit ? '3' : '1'; upper.style.zIndex = '2';
        lower.setAttribute('aria-valuemax', String(high)); upper.setAttribute('aria-valuemin', String(low));
        lower.setAttribute('aria-valuetext', money.format(low)); upper.setAttribute('aria-valuetext', money.format(high));
        group.querySelector('[data-price-start]').textContent = money.format(low);
        group.querySelector('[data-price-end]').textContent = money.format(high);
      }
      function fromFields() { if (validate()) paint(...values()); }
      function fromRange(handle, amount = Number(handle.value)) {
        const [low, high] = [Number(lower.value), Number(upper.value)];
        const value = Math.round(Math.max(0, Math.min(limit, handle === lower ? Math.min(amount, high) : Math.max(amount, low))) * 100) / 100;
        // Keep untouched empty limits absent from the URL until the shopper changes them.
        if (handle === lower) min.value = String(value); else max.value = String(value);
        paint(handle === lower ? value : low, handle === upper ? value : high); validate();
      }
      listen(lower, 'input', () => fromRange(lower)); listen(upper, 'input', () => fromRange(upper));
      listen(min, 'input', fromFields); listen(max, 'input', fromFields);
      let dragged = null;
      const amountAt = x => { const rect = track.getBoundingClientRect(); return Math.max(0, Math.min(1, (x - rect.left - 12) / Math.max(1, rect.width - 24))) * limit; };
      listen(track, 'pointerdown', event => {
        if (!limit || event.button !== 0 || event.target.matches('input')) return;
        const amount = amountAt(event.clientX), low = Number(lower.value), high = Number(upper.value);
        dragged = Math.abs(amount - low) < Math.abs(amount - high) || (low === high && amount <= low) ? lower : upper;
        dragged.focus({ preventScroll: true }); track.setPointerCapture(event.pointerId); fromRange(dragged, amount);
      });
      listen(track, 'pointermove', event => { if (dragged) fromRange(dragged, amountAt(event.clientX)); });
      listen(track, 'pointerup', () => { dragged = null; }); listen(track, 'pointercancel', () => { dragged = null; });
      lower.disabled = upper.disabled = limit === 0;
      fromFields(); group.querySelector('[data-price-slider]').hidden = false;
      return { validate };
    });
    function validatePrices() {
      priceFilters.forEach(price => price.validate());
    }
    listen(form, 'input', validatePrices);
    listen(form, 'submit', event => {
      event.preventDefault();
      validatePrices();
      if (!form.reportValidity()) return;
      const url = new URL(form.action, location.href);
      // Shopify evaluates filters and sorting across the entire collection.
      // Submitting new criteria deliberately drops the old page number.
      const params = new URLSearchParams();
      for (const [name, value] of new FormData(form)) if (String(value).trim() !== '') params.append(name, String(value));
      url.search = params.toString();
      url.hash = '';
      location.assign(url.href);
    });
    listen(form, 'change', event => { if (!mobile.matches && event.target.matches('input[type="checkbox"]')) form.requestSubmit(); });
    listen(root.querySelector('[data-collection-sort]'), 'change', () => form.requestSubmit());
    return { destroy() { animation?.cancel(); controller.abort(); if (dialog.open) dialog.close(); sidebar.append(panel); } };
  }
  function initialize(scope) {
    const roots = [...scope.querySelectorAll('[data-main-collection]')];
    if (scope.matches?.('[data-main-collection]')) roots.unshift(scope);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, create(root)); });
  }
  function remove(root) { instances.get(root)?.destroy(); instances.delete(root); }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => { [...instances.keys()].forEach(root => { if (event.target === root || event.target.contains(root)) remove(root); }); });
  new MutationObserver(() => { [...instances.keys()].forEach(root => { if (!root.isConnected) remove(root); }); }).observe(document.documentElement, { childList: true, subtree: true });
  window[key] = initialize;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
})();
